import { useRef, useCallback } from 'react'
import { useTranslation } from 'next-i18next'
import { makeStyles, Container } from '@material-ui/core'
import T from '../home-new/tokens'
import Reveal from '../home-new/Reveal'

// Section 3 — "สิทธิประโยชน์สำหรับลูกค้า SHIPPOP BUSINESS".
// Bento layout: 6 text blocks interleaved with 4 wide illustration cards
// (benefits/01–04), on a 3-column grid. Collapses to a single column on
// mobile, following DOM order (image then its related text).
//
// "Magic Bento" interaction (reactbits.dev/components/magic-bento), adapted
// from DARK+PURPLE to our LIGHT section with a BRAND-BLUE glow
// (rgb(0,158,255)). Reimplemented dependency-free (no GSAP) — the same
// vanilla technique the /new FeaturesNew already uses: one rAF-throttled
// pointer handler writes CSS custom properties (--glow-x/--glow-y per card,
// --spot-x/--spot-y for the section spotlight) and a transform; all visuals
// are CSS transform/opacity only. Effects kept: (1) grid cursor spotlight,
// (2) per-card masked border glow, (3) subtle tilt, (4) magnetism + lift,
// (6) click ripple (Web Animations API). Particles (5) are intentionally
// DROPPED — glowing dots read as noise on light cards rather than premium
// glow (see report). prefers-reduced-motion → glows stay static (centred),
// no tilt/magnetism/ripple.

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '70px 0',
    background: T.color.surface,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      padding: '44px 0',
    },
  },
  heading: {
    fontFamily: T.font.heading,
    fontSize: 32,
    fontWeight: 700,
    color: T.color.ink,
    textAlign: 'center',
    margin: '0 0 44px',
    '& span': { color: T.color.brand },
    [theme.breakpoints.down('sm')]: {
      fontSize: 22,
      margin: '0 0 28px',
    },
  },
  gridWrap: {
    position: 'relative',
    borderRadius: 24,
    '&:hover $spotlight': { opacity: 1 },
  },
  // Global cursor spotlight — a soft brand-blue radial glow following the
  // pointer, painted BEHIND the (opaque) cards so it glows in the gutters
  // and around the grid without washing any card text.
  spotlight: {
    position: 'absolute',
    inset: '-40px',
    zIndex: 0,
    pointerEvents: 'none',
    opacity: 0,
    transition: 'opacity 0.45s ease',
    background:
      'radial-gradient(600px circle at var(--spot-x, 50%) var(--spot-y, 50%), rgba(0,158,255,0.16), rgba(0,158,255,0) 45%)',
    [theme.breakpoints.down('sm')]: { display: 'none' },
  },
  grid: {
    position: 'relative',
    zIndex: 1,
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gridTemplateAreas: `
      "t1 i1 i1"
      "i2 t2 t2"
      "t3 i3 i3"
      "i4 t4 t5"
    `,
    gap: '22px',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
  },
  // Shared Magic-Bento card treatment (applied on top of textCard/imgCard).
  card: {
    position: 'relative',
    isolation: 'isolate',
    overflow: 'hidden',
    willChange: 'transform',
    transition:
      'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.3s ease',
    // (1b) soft inner glow toward the cursor
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      zIndex: -1,
      borderRadius: 'inherit',
      pointerEvents: 'none',
      opacity: 0,
      transition: 'opacity 0.4s ease',
      background:
        'radial-gradient(260px circle at var(--glow-x, 50%) var(--glow-y, 50%), rgba(0,158,255,0.12), transparent 60%)',
    },
    // (2) border glow — brand-blue ring brightest nearest the cursor,
    // masked to the card's border via content-box/border-box exclude.
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      zIndex: -1,
      borderRadius: 'inherit',
      pointerEvents: 'none',
      padding: '1.5px',
      opacity: 0,
      transition: 'opacity 0.4s ease',
      background:
        'radial-gradient(320px circle at var(--glow-x, 50%) var(--glow-y, 50%), rgba(0,158,255,0.9), rgba(0,158,255,0.25) 55%, transparent 75%)',
      WebkitMask:
        'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      maskComposite: 'exclude',
    },
    '&:hover': {
      boxShadow: '0 16px 36px rgba(0,102,204,0.16)',
    },
    '&:hover::before, &:hover::after': { opacity: 1 },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'box-shadow 0.3s ease',
      '&:hover::before': { opacity: 0.5 },
      '&:hover::after': { opacity: 0.6 },
    },
  },
  textCard: {
    background: T.color.surface,
    borderRadius: 20,
    boxShadow: T.shadow.card,
    padding: '32px 30px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: '24px 22px',
    },
  },
  imgCard: {
    background: T.color.canvasAlt,
    borderRadius: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 220,
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    [theme.breakpoints.down('sm')]: {
      minHeight: 'auto',
    },
  },
  // Title sized to the mockup's drawn benefit-card title cap-height
  // (business.png "Credit Term" caps ~24px); kept equal to the /business
  // BusinessTypesBiz card title for a consistent card rhythm on the page.
  cardTitle: {
    fontFamily: T.font.heading,
    fontSize: 24,
    fontWeight: 600,
    color: T.color.brand,
    margin: '0 0 12px',
    [theme.breakpoints.down('sm')]: {
      fontSize: 20,
    },
  },
  // Desc to the mockup's drawn body size (~14px).
  cardDesc: {
    fontFamily: T.font.body,
    fontSize: 14,
    color: T.color.muted,
    lineHeight: 1.7,
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      fontSize: 13,
    },
  },
  a_t1: { gridArea: 't1' },
  a_t2: { gridArea: 't2' },
  a_t3: { gridArea: 't3' },
  a_t4: { gridArea: 't4' },
  a_t5: { gridArea: 't5' },
  a_i1: { gridArea: 'i1' },
  a_i2: { gridArea: 'i2' },
  a_i3: { gridArea: 'i3' },
  a_i4: { gridArea: 'i4' },
}))

// Pointer-driven Magic-Bento behaviour. Pure DOM/rAF (no React state) so
// moving the cursor never re-renders — only rAF-batched inline-style writes.
const prefersReduced = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const useMagicBento = () => {
  const raf = useRef(null)

  const onGridMove = useCallback((e) => {
    if (prefersReduced()) return
    const el = e.currentTarget
    const r = el.getBoundingClientRect()
    el.style.setProperty('--spot-x', `${e.clientX - r.left}px`)
    el.style.setProperty('--spot-y', `${e.clientY - r.top}px`)
  }, [])

  const onCardMove = useCallback((e) => {
    if (prefersReduced()) return
    const el = e.currentTarget
    const r = el.getBoundingClientRect()
    if (!r.width || !r.height) return
    const x = e.clientX - r.left
    const y = e.clientY - r.top
    const px = (x / r.width) * 100
    const py = (y / r.height) * 100
    // Subtle tilt (≤4°) + magnetism (≤6px toward cursor) + lift + tiny scale.
    const rotateY = ((x / r.width) - 0.5) * 8
    const rotateX = -((y / r.height) - 0.5) * 8
    const magX = ((x / r.width) - 0.5) * 6
    const magY = ((y / r.height) - 0.5) * 6

    if (raf.current) cancelAnimationFrame(raf.current)
    raf.current = requestAnimationFrame(() => {
      el.style.setProperty('--glow-x', `${px}%`)
      el.style.setProperty('--glow-y', `${py}%`)
      el.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translate(${magX}px, ${magY - 5}px) scale(1.012)`
    })
  }, [])

  const onCardLeave = useCallback((e) => {
    const el = e.currentTarget
    if (raf.current) cancelAnimationFrame(raf.current)
    raf.current = requestAnimationFrame(() => {
      el.style.transform = ''
      el.style.removeProperty('--glow-x')
      el.style.removeProperty('--glow-y')
    })
  }, [])

  // (6) Click ripple — a soft brand-blue pulse from the pointer, animated
  // with the Web Animations API (transform/opacity only, self-cleaning).
  const onCardDown = useCallback((e) => {
    if (prefersReduced() || typeof document === 'undefined') return
    const el = e.currentTarget
    const r = el.getBoundingClientRect()
    const size = Math.max(r.width, r.height) * 1.1
    const span = document.createElement('span')
    span.style.cssText = [
      'position:absolute',
      `left:${e.clientX - r.left}px`,
      `top:${e.clientY - r.top}px`,
      `width:${size}px`,
      `height:${size}px`,
      `margin-left:${-size / 2}px`,
      `margin-top:${-size / 2}px`,
      'border-radius:50%',
      'pointer-events:none',
      'z-index:-1',
      'background:radial-gradient(circle, rgba(0,158,255,0.32), rgba(0,158,255,0) 70%)',
    ].join(';')
    el.appendChild(span)
    if (typeof span.animate === 'function') {
      const anim = span.animate(
        [
          { transform: 'scale(0)', opacity: 0.6 },
          { transform: 'scale(1)', opacity: 0 },
        ],
        { duration: 600, easing: 'ease-out' }
      )
      anim.onfinish = () => span.remove()
    } else {
      setTimeout(() => span.remove(), 600)
    }
  }, [])

  return { onGridMove, onCardMove, onCardLeave, onCardDown }
}

const BenefitsBiz = () => {
  const classes = useStyles()
  const { t } = useTranslation('business')
  const mb = useMagicBento()

  const cardHandlers = {
    onMouseMove: mb.onCardMove,
    onMouseLeave: mb.onCardLeave,
    onMouseDown: mb.onCardDown,
  }

  const TextCard = ({ area, id }) => (
    <div className={`${classes.card} ${classes.textCard} ${area}`} {...cardHandlers}>
      <h3 className={classes.cardTitle}>{t(`benefits.items.${id}.title`)}</h3>
      <p className={classes.cardDesc}>{t(`benefits.items.${id}.desc`)}</p>
    </div>
  )

  // webp sibling generated next to each .png; card CSS (objectFit) drives
  // display. Below the fold → lazy. display:contents keeps the picture
  // layout-transparent so the card's `& img` sizing still applies.
  const ImgCard = ({ area, src, alt }) => (
    <div className={`${classes.card} ${classes.imgCard} ${area}`} {...cardHandlers}>
      <picture style={{ display: 'contents' }}>
        <source srcSet={src.replace(/\.png$/, '.webp')} type="image/webp" />
        <img src={src} alt={alt} loading="lazy" decoding="async" />
      </picture>
    </div>
  )

  return (
    <section className={classes.root} id="benefits">
      <Container maxWidth={false} style={{ maxWidth: T.container }}>
        <Reveal>
          <h2 className={classes.heading}>
            {t('benefits.headingPre')}<span>{t('benefits.headingSpan')}</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className={classes.gridWrap} onMouseMove={mb.onGridMove}>
            <span className={classes.spotlight} aria-hidden="true" />
            <div className={classes.grid}>
              <TextCard area={classes.a_t1} id="t1" />
              <ImgCard area={classes.a_i1} src="/static/img/home-business/benefits/01.png" alt={t('benefits.items.t1.title')} />
              <ImgCard area={classes.a_i2} src="/static/img/home-business/benefits/02.png" alt={t('benefits.items.t2.title')} />
              <TextCard area={classes.a_t2} id="t2" />
              <TextCard area={classes.a_t3} id="t3" />
              <ImgCard area={classes.a_i3} src="/static/img/home-business/benefits/04.png" alt={t('benefits.items.t4.title')} />
              <ImgCard area={classes.a_i4} src="/static/img/home-business/benefits/03.png" alt={t('benefits.items.t3.title')} />
              <TextCard area={classes.a_t4} id="t4" />
              <TextCard area={classes.a_t5} id="t5" />
            </div>
          </div>
        </Reveal>
      </Container>
    </section>
  )
}

export default BenefitsBiz
